<template>
    <QrWrapper>
        <div v-if="qrCode" class="max-w-7xl w-full space-y-4 pb-4">
            <QrHeader class="flex justify-between items-center">
                <div class="flex items-center">
                    <QrIcon icon="qrcode" class="mr-2"/>
                    <QrParagraph :text="trans('qrcode.edit')" trans="qrcode.edit"/>
                </div>
            </QrHeader>

            <div class="lg:grid grid-cols-12 gap-4">
                <QrCard class="col-span-8">
                    <div class="space-y-4">
                        <div class="flex space-x-2">
                            <QrInput
                                v-model="qrCode.name"
                                :label="trans('qrcode.name')"
                                :errors="validationErrors.name"
                                class="w-full"
                            />

                            <QrButton
                                positive
                                class="mt-[24px]"
                                :text="trans('button.save')"
                                trans="button.save"
                                :loading="loading"
                                :recently-succeeded="recentlySucceeded"
                                :recently-failed="recentlyFailed"
                                @click="qrCodeStore.update"
                            />
                        </div>

                        <div>
                            <QrParagraph :text="trans('qrcode.short-link')" trans="qrcode.short-link"/>
                            <QrLink :href="qrCode.url" target="_blank">
                                {{ qrCode.url }}
                                <QrIcon icon="arrow-up-right-from-square" class="ml-1"/>
                            </QrLink>
                        </div>

                        <OrderStatus v-if="qrCode.order" :qr-code="qrCode"/>
                        <div v-else>
                            <div class="inline-block">
                                <QrParagraph class="inline-block" :text="trans('label.product-name')" trans="label.product-name"/>
                                <QrParagraph class="inline-block font-semibold" :text="`: ${qrCode.product.name}`"/>
                            </div>

                            <div>
                                <QrParagraph class="inline-block" :text="trans('label.price-per-chip')" trans="label.price-per-chip"/>
                                <QrParagraph class="inline-block font-semibold" :text="`: ${qrCode.product.price_readable}`"/>
                            </div>

                            <div class="flex justify-between mt-4">
                                <Order :qr-code="qrCode"/>
                                <DeleteQrCode :qr-code="qrCode"/>
                            </div>
                        </div>
                    </div>
                </QrCard>

                <QrCard class="col-span-4 lg:mt-0 mt-4" flex>
                    <div class="my-auto w-full">
                        <!-- Used to generate the image part of the QR code -->
                        <ImageCrop v-if="previewLoaded && qrCode.image" :model="qrCode" :file="qrCode.image.file" class="hidden"/>
                        <Preview :qr-code="qrCode" @loaded="previewLoaded = true"/>

                        <div v-if="canEditQrCode && qrCode.product.allows_image" class="flex justify-between mt-6">
                            <QrButton
                                secondary
                                :text="trans('button.crop-image')"
                                trans="button.crop-image"
                                icon="frame"
                                @click="openCropModal"
                            />

                            <QrButton
                                secondary
                                :text="trans('button.new-image')"
                                trans="button.new-image"
                                icon-right="image-landscape"
                                @click="fileModal = true"
                            />
                        </div>
                    </div>

                    <QrModal v-model="cropModal" :close-text="trans('button.close')" @onClose="resetCropPosition">
                        <ImageCrop
                            v-if="previewLoaded"
                            ref="cropper"
                            :model="qrCode"
                            :file="qrCode.image.file"
                            class="col-span-2"
                        />

                        <template #buttons>
                            <QrButton
                                positive
                                :text="trans('button.save')"
                                trans="button.save"
                                class="ml-auto"
                                :loading="loading"
                                :recently-succeeded="recentlySucceeded"
                                :recently-failed="recentlyFailed"
                                @click="qrCodeStore.update(() => cropModal = false)"
                            />
                        </template>
                    </QrModal>

                    <QrModal v-model="fileModal" :close-text="trans('button.close')">
                        <QrFilesUpload
                            v-if="qrCode.product.allows_image"
                            class="col-span-4"
                            :upload-files="uploadFiles"
                            :helper-text="trans('qrcode.helper-text')"
                        />
                    </QrModal>
                </QrCard>
            </div>

            <QrCard v-if="canEditQrCode" :header="trans('qrcode.qr-code-content')" trans="qrcode.qr-code-content">
                <Message info :header="trans('code.qr-cannot-be-changed-after-ordering')" header-trans="code.qr-cannot-be-changed-after-ordering"/>

                <div v-if="!qrCode.product.allows_image" class="mb-4">
                    <QrParagraph :text="trans('qrcode.text-above')" trans="qrcode.text-above"/>
                    <div class="flex justify-between">
                        <QrInput
                            v-model="qrCode.text_top"
                            :errors="validationErrors.text_top"
                            class="w-full"
                        />
                        <StyleSelection
                            :style="qrCode.text_top_style"
                            @change="style => qrCode.text_top_style = style"
                            class="ml-2"
                        />
                    </div>
                </div>

                <div>
                    <QrParagraph :text="trans('qrcode.text-below')" trans="qrcode.text-below"/>
                    <div class="flex justify-between">
                        <QrInput
                            v-model="qrCode.text_bottom"
                            :errors="validationErrors.text_bottom"
                            class="w-full"
                        />
                        <StyleSelection
                            :style="qrCode.text_bottom_style"
                            @change="style => qrCode.text_bottom_style = style"
                            class="ml-2"
                        />
                    </div>
                </div>

                <div class="flex mt-4">
                    <QrButton
                        small
                        positive
                        class="ml-auto"
                        :text="trans('button.save')"
                        trans="button.save"
                        :loading="loading"
                        :recently-succeeded="recentlySucceeded"
                        :recently-failed="recentlyFailed"
                        @click="qrCodeStore.update"
                    />
                </div>
            </QrCard>

            <QrCard :header="trans('qrcode.type')" header-trans="qrcode.type">
                <QrForm @submit.prevent="qrCodeStore.update">
                    <QrRadio
                        v-model="qrCode.type"
                        horizontal
                        :options="[
                            { text: trans('option.i-want-to-point-to-a-website'), trans: 'option.i-want-to-point-to-a-website', value: 'webpage' },
                            { text: trans('option.i-want-to-create-a-home-page'), trans: 'option.i-want-to-create-a-home-page', value: 'homepage' },
                        ]"
                        class="mb-4"
                    />
                    <QrInput
                        v-if="qrCode.type === 'webpage'"
                        v-model="qrCode.redirect_url"
                        :errors="validationErrors.redirect_url"
                        :label="trans('qrcode.address')"
                        trans="qrcode.address"
                    />
                    <div v-else class="space-y-4">
                        <QrInput
                            v-model="qrCode.homepage.title_current"
                            :label="trans('homepage.title')"
                            trans="homepage.title"
                        />

                        <QrInput
                            v-model="qrCode.homepage.sub_title_current"
                            :label="trans('homepage.sub-title')"
                            trans="homepage.sub-title"
                        />

                        <QrToggle
                            v-model="qrCode.homepage.is_password_protected"
                            :label="trans('homepage.protect-with-password')"
                            label-trans="homepage.protect-with-password"
                        />
                        <QrInput
                            v-if="qrCode.homepage.is_password_protected"
                            v-model="qrCode.homepage.password"
                            :label="trans('homepage.password')"
                            trans="homepage.password"
                        />
                    </div>

                    <div class="flex mt-4">
                        <QrButton
                            small
                            positive
                            type="submit"
                            :text="trans('button.save')"
                            trans="button.save"
                            class="ml-auto"
                            :loading="loading"
                            :recently-succeeded="recentlySucceeded"
                            :recently-failed="recentlyFailed"
                        />
                    </div>
                </QrForm>
            </QrCard>

            <QrCard v-if="qrCode.type === 'homepage'" :header="trans('homepage.builder')" trans="homepage.builder">
                <template #buttons>
                    <QrButton
                        :href="`/code/${qrCode.uid}/homepage?preview=1`"
                        :text="trans('button.view-homepage')" trans="button.view-homepage"
                        icon-right="arrow-up-right-from-square"
                        target="_blank"
                        small
                    />
                </template>
                <HomepageBuilder/>
            </QrCard>
        </div>
    </QrWrapper>
</template>

<script setup>
import { useQrCodesStore} from "@/store/qrCodes";
import { trans } from "@/libs/helper.js";
import { onBeforeMount, ref } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import QrButton from "@/components/application-ui/elements/QrButton.vue";
import QrInput from "@/components/application-ui/form/QrInput.vue";
import QrParagraph from "@/components/application-ui/content/QrParagraph.vue";
import QrLink from "@/components/application-ui/content/QrLink.vue";
import StyleSelection from "@/components/qr-code/StyleSelection.vue";
import Preview from "@/components/qr-code/Preview.vue";
import QrFilesUpload from "@/components/application-ui/form/QrFilesUpload.vue";
import createUploader from "@/libs/file-uploader.js";
import ImageCrop from "@/components/qr-code/ImageCrop.vue";
import QrHeader from "@/components/application-ui/content/QrHeader.vue";
import QrWrapper from "@/components/application-ui/panels/QrWrapper.vue";
import QrRadio from "@/components/application-ui/form/QrRadio.vue";
import QrCard from "@/components/application-ui/panels/QrCard.vue";
import QrModal from "@/components/application-ui/dialogs/QrModal.vue";
import HomepageBuilder from "@/components/qr-code/homepage/HomepageBuilder.vue";
import Order from "@/components/qr-code/Order.vue";
import QrToggle from "@/components/application-ui/form/QrToggle.vue";
import { useStatesStore } from "@/store/states.js";
import Message from "@/components/application-ui/feedback/Message.vue";
import OrderStatus from "@/components/qr-code/OrderStatus.vue";
import QrForm from "@/components/application-ui/content/QrForm.vue";
import DeleteQrCode from "@/components/qr-code/DeleteQrCode.vue";

const route = useRoute();
const statesStore = useStatesStore();
const { loading, validationErrors, recentlySucceeded, recentlyFailed } = storeToRefs(statesStore);
const qrCodeStore = useQrCodesStore();
const { qrCode, canEditQrCode } = storeToRefs(qrCodeStore);
const previewLoaded = ref(false);
const cropModal = ref(false);
const initialCropPosition = ref({});
const fileModal = ref(false);
const cropper = ref(null);

const openCropModal = () => {
    initialCropPosition.value = qrCode.value.crop_position.cropper
    cropModal.value = true;
};

const resetCropPosition = () => {
    qrCode.value.crop_position.cropper = initialCropPosition.value
    cropper.value.reset(qrCode.value.crop_position.cropper)
};

const uploadComplete = ({ status, data }) => {
    if (status === 200) {
        qrCode.value = data.data
    }
};

const { uploadFiles } = createUploader(`/api/qr-codes/${route.params.qrCode}/image`, uploadComplete);

onBeforeMount(async () => {
    await qrCodeStore.fetchOne(route.params.qrCode)
});
</script>
